import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Swords, Shield, Target, Skull, CircleDot, Radio, Gamepad2, SlidersHorizontal,
  ListChecks, History, MonitorPlay, Activity,
} from "lucide-react";
import { api } from "../lib/api";
import { useRealtime } from "../lib/realtime";

const TILES = [
  { to: "/attack", label: "Attacco", icon: Swords, color: "#2563eb", testid: "tile-attack" },
  { to: "/defense", label: "Difesa", icon: Shield, color: "#ea580c", testid: "tile-defense" },
  { to: "/objectives", label: "Obiettivi", icon: Target, color: "#f59e0b", testid: "tile-objectives" },
  { to: "/penalties", label: "Penitenze", icon: Skull, color: "#dc2626", testid: "tile-penalties" },
  { to: "/custom", label: "Ruote Custom", icon: CircleDot, color: "#7c3aed", testid: "tile-custom" },
  { to: "/streaming", label: "Streaming", icon: Radio, color: "#10b981", testid: "tile-streaming" },
  { to: "/stream-deck", label: "Stream Deck", icon: Gamepad2, color: "#0ea5e9", testid: "tile-streamdeck" },
  { to: "/settings", label: "Impostazioni", icon: SlidersHorizontal, color: "#64748b", testid: "tile-settings" },
];

const SECONDARY = [
  { to: "/content", label: "Gestione contenuti", icon: ListChecks },
  { to: "/history", label: "Cronologia", icon: History },
  { to: "/overlay-editor", label: "Overlay OBS", icon: MonitorPlay },
];

export default function Dashboard() {
  const [status, setStatus] = useState(null);
  const [last, setLast] = useState(null);
  useRealtime((ev) => {
    if (ev.type === "WHEEL_SPIN" || ev.type === "OBJECTIVE_DRAW" || ev.type === "SUB_PENALTY")
      setLast(ev);
  });
  useEffect(() => {
    api.integrationsStatus().then(setStatus).catch(() => {});
    const t = setInterval(() => api.integrationsStatus().then(setStatus).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-5 md:p-8 tactical-grid min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="text-amber-500 font-semibold tracking-widest text-xs uppercase mb-2">
            Rainbow Six Siege · Streamer Control Center
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tight text-white leading-none">
            Siege Stream Wheel
          </h1>
          <p className="text-slate-400 mt-3 max-w-2xl">
            Controlla ruote operatori, obiettivi e penitenze in tempo reale. Overlay OBS, Twitch e Stream Deck sincronizzati da un unico motore.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className="group relative overflow-hidden bg-[#131722] border border-[#334155] rounded-xl p-6 min-h-[130px] flex flex-col justify-between hover:border-amber-500/60 transition-colors"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${t.color}22`, border: `1px solid ${t.color}66` }}
                >
                  <Icon className="w-6 h-6" style={{ color: t.color }} />
                </div>
                <span className="font-display text-2xl md:text-3xl font-extrabold uppercase tracking-wide text-white group-hover:text-amber-400 transition-colors">
                  {t.label}
                </span>
                <div className="absolute -right-6 -bottom-6 opacity-[0.06] group-hover:opacity-[0.12] transition-opacity">
                  <Icon className="w-28 h-28" style={{ color: t.color }} />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 bg-[#131722] border border-[#334155] rounded-xl p-5">
            <div className="flex items-center gap-2 text-slate-300 font-semibold mb-3">
              <Activity className="w-4 h-4 text-amber-400" /> Ultimo evento
            </div>
            {last ? (
              <div className="animate-sw-slide-up" data-testid="dashboard-last-event">
                <div className="text-xs uppercase tracking-widest text-slate-500">
                  {last.type} · {last.source}
                </div>
                <div className="font-display text-3xl font-black text-amber-300 uppercase mt-1">
                  {last.result || last.title}
                </div>
              </div>
            ) : (
              <div className="text-slate-500 text-sm">Nessun evento ancora. Avvia una ruota per iniziare.</div>
            )}
          </div>
          <div className="bg-[#131722] border border-[#334155] rounded-xl p-5">
            <div className="text-slate-300 font-semibold mb-3">Stato integrazioni</div>
            <StatusRow label="Overlay OBS" state={status?.overlay} testid="dash-status-overlay" />
            <StatusRow label="Realtime" state={status?.realtime} testid="dash-status-realtime" />
            <StatusRow label="Stream Deck" state={status?.streamdeck} testid="dash-status-streamdeck" />
            <StatusRow label="Twitch" state={status?.twitch} testid="dash-status-twitch" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          {SECONDARY.map((s) => {
            const Icon = s.icon;
            return (
              <Link key={s.to} to={s.to} className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 bg-[#11141d] border border-[#1e293b] rounded-lg px-4 py-2.5 transition-colors">
                <Icon className="w-4 h-4" /> {s.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, state, testid }) {
  const map = {
    connected: { c: "#10b981", t: "Connesso" },
    waiting: { c: "#eab308", t: "In attesa" },
    disconnected: { c: "#ef4444", t: "Disconnesso" },
  };
  const s = map[state] || map.disconnected;
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#1e293b] last:border-0" data-testid={testid}>
      <span className="text-slate-300 text-sm">{label}</span>
      <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: s.c }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.c, boxShadow: `0 0 8px ${s.c}` }} />
        {s.t}
      </span>
    </div>
  );
}
